import { useEffect } from "react";
import { useHistoryStore } from "../../stores/historyStore.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import type { HistoryNode } from "../../infra/api.js";
import { Badge } from "../../ui/Badge.js";
import { Button } from "../../ui/Button.js";
import { Card } from "../../ui/Card.js";

/** Single-glyph actor mark — keeps the tree dense without an icon set. */
const ACTOR_GLYPH: Record<string, string> = {
  user: "you",
  ai: "ai",
  system: "sys",
};

/** Relative "x ago" label from an ISO timestamp; falls back to the raw string. */
function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 60) return "agora";
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min atrás`;
  const deltaH = Math.round(deltaMin / 60);
  if (deltaH < 24) return `${deltaH} h atrás`;
  const deltaD = Math.round(deltaH / 24);
  return `${deltaD} d atrás`;
}

export function HistoryTree(): JSX.Element | null {
  const projectId = useCompositionStore((s) => s.projectId);
  const lastGlobalSeq = useCompositionStore((s) => s.lastGlobalSeq);

  const nodes = useHistoryStore((s) => s.nodes);
  const branches = useHistoryStore((s) => s.branches);
  const loading = useHistoryStore((s) => s.loading);
  const error = useHistoryStore((s) => s.error);
  const travelEventId = useHistoryStore((s) => s.travelEventId);
  const travelLoading = useHistoryStore((s) => s.travelLoading);
  const travelError = useHistoryStore((s) => s.travelError);
  const branchStatus = useHistoryStore((s) => s.branchStatus);
  const branchError = useHistoryStore((s) => s.branchError);

  // Refetch the projection whenever the live log advances — a new event the SSE
  // stream delivered means a new node to draw. `lastGlobalSeq` is the cheapest
  // signal that "something happened".
  useEffect(() => {
    if (!projectId) return;
    void useHistoryStore.getState().load(projectId);
  }, [projectId, lastGlobalSeq]);

  useEffect(() => {
    return () => useHistoryStore.getState().reset();
  }, []);

  if (!projectId) return null;

  function onTravel(node: HistoryNode): void {
    void useHistoryStore.getState().travelTo(node.eventId);
  }

  function onBranch(node: HistoryNode): void {
    const label = window.prompt(
      "Nome do ramo a partir deste ponto:",
      `Ramo ${branches.length + 1}`,
    );
    if (label === null || label.trim() === "") return;
    void useHistoryStore
      .getState()
      .branch(node.eventId, label.trim())
      .catch(() => {
        // error recorded in branchError
      });
  }

  // Newest node on top — the trunk reads present → past, like an undo stack.
  const ordered = [...nodes].sort((a, b) => b.globalSeq - a.globalSeq);
  const branchLabelById = new Map(branches.map((b) => [b.branchId, b.label]));

  return (
    <Card aria-label="History tree" className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
            Histórico da música
          </span>
          <Badge tone="neutral" aria-label={`${nodes.length} eventos`}>
            {nodes.length} {nodes.length === 1 ? "evento" : "eventos"}
          </Badge>
        </div>
        {branchStatus === "creating" ? (
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-butter">
            ramificando…
          </span>
        ) : null}
      </header>

      {travelEventId !== null ? (
        <div
          role="status"
          className="flex items-center justify-between gap-3 border border-butter bg-butter/10 px-4 py-3"
        >
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[11px] uppercase tracking-widest2 text-butter">
              Você está vendo o passado
            </span>
            <span className="text-xs text-ink-muted">
              {travelLoading
                ? "Materializando o estado deste ponto…"
                : "Modo leitura — nenhuma edição é anexada ao log."}
            </span>
          </div>
          <Button
            variant="primary"
            aria-label="Voltar ao presente"
            onClick={() => useHistoryStore.getState().returnToPresent()}
          >
            Voltar ao presente
          </Button>
        </div>
      ) : null}

      {error ? (
        <span role="alert" className="text-xs text-accent-danger">
          {error}
        </span>
      ) : null}
      {branchStatus === "error" && branchError ? (
        <span role="alert" className="text-xs text-accent-danger">
          {branchError}
        </span>
      ) : null}
      {travelError ? (
        <span role="alert" className="text-xs text-accent-danger">
          {travelError}
        </span>
      ) : null}

      {loading && nodes.length === 0 ? (
        <p className="font-mono text-[11px] uppercase tracking-widest2 text-ink-dim">
          carregando histórico…
        </p>
      ) : null}

      {!loading && nodes.length === 0 ? (
        <div className="border border-dashed border-border bg-asphalt/40 px-4 py-6">
          <span className="font-mono text-[11px] uppercase tracking-widest2 text-paper">
            Sem histórico ainda
          </span>
          <p className="mt-1 max-w-md text-xs leading-relaxed text-ink-muted">
            Cada comando, geração e promoção vira um ponto navegável aqui.
          </p>
        </div>
      ) : null}

      {ordered.length > 0 ? (
        <ol className="flex flex-col" aria-label="History timeline">
          {ordered.map((node) => {
            const traveling = node.eventId === travelEventId;
            const onBranch_ = node.branchId !== null;
            return (
              <li
                key={node.eventId}
                className={`flex items-stretch gap-3 ${onBranch_ ? "pl-6" : ""}`}
              >
                {/* Trunk rail: a vertical line with a node dot. Branch nodes are
                    indented and use a hollow dot to read as a divergence. */}
                <div aria-hidden="true" className="relative flex w-3 shrink-0 justify-center">
                  <span className="absolute inset-y-0 w-px bg-border" />
                  <span
                    className={`relative mt-3 h-2 w-2 shrink-0 rounded-full border ${
                      traveling
                        ? "border-butter bg-butter"
                        : onBranch_
                          ? "border-oscillo bg-tape"
                          : "border-borderHi bg-paper"
                    }`}
                  />
                </div>

                <div
                  className={`flex flex-1 flex-col gap-2 border-b border-border py-3 ${
                    traveling ? "bg-butter/5" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      aria-label={`Ver estado em ${node.summary}`}
                      aria-current={traveling ? "true" : undefined}
                      onClick={() => onTravel(node)}
                      className="min-w-0 flex-1 text-left transition-colors hover:text-butter"
                    >
                      <span className="block truncate text-sm text-paper">{node.summary}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
                        <span aria-label={`Autor: ${node.createdBy}`}>
                          {ACTOR_GLYPH[node.createdBy] ?? node.createdBy}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span>{relativeTime(node.createdAt)}</span>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">#{node.globalSeq}</span>
                        {node.branchId !== null ? (
                          <Badge tone="neutral" aria-label="Ramo">
                            {branchLabelById.get(node.branchId) ?? "ramo"}
                          </Badge>
                        ) : null}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      aria-label={`Ramificar a partir de ${node.summary}`}
                      disabled={branchStatus === "creating"}
                      onClick={() => onBranch(node)}
                    >
                      Ramificar daqui
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
    </Card>
  );
}
